import type { ArtifactTypeDefinition } from './ArtifactTypeDefinition';

/** Maximum characters shown in panel-mode artifact preview cards. */
export const ARTIFACT_PREVIEW_MAX_CHARS = 200;

const artifactTypeRegistry = new Map<string, ArtifactTypeDefinition>();

/**
 * Registers or replaces an artifact type definition in the UI registry.
 * @param definition Per-type metadata and renderer component
 */
export function registerArtifactType(definition: ArtifactTypeDefinition): void {
  artifactTypeRegistry.set(definition.type, definition);
}

/**
 * Returns the registered definition for an artifact MIME type.
 * @param type Artifact MIME type
 */
export function getArtifactType(type: string): ArtifactTypeDefinition | undefined {
  return artifactTypeRegistry.get(type);
}

/**
 * Returns all registered artifact type definitions.
 */
export function getRegisteredArtifactTypes(): ArtifactTypeDefinition[] {
  return [...artifactTypeRegistry.values()];
}

/**
 * Returns whether the artifact type stores a pointer rather than downloadable body content.
 * @param type Artifact MIME type
 */
export function isPointerArtifactType(type: string): boolean {
  return getArtifactType(type)?.isPointer ?? false;
}

/**
 * Returns the text copied by the artifact copy button, or null when copy is unavailable.
 * Pointer types must opt in with `getCopyText`. Other types copy the artifact body.
 * @param type Artifact MIME type
 * @param content Raw artifact body or pointer payload
 */
export function getArtifactCopyText(type: string, content: string): string | null {
  const definition = getArtifactType(type);
  if (definition?.getCopyText !== undefined) {
    return definition.getCopyText(content);
  }
  if (definition?.isPointer === true) {
    return null;
  }
  return content.length > 0 ? content : null;
}

/**
 * Builds preview text for artifact cards in panel mode.
 * @param type Artifact MIME type
 * @param content Raw artifact body or pointer payload
 */
export function getArtifactPreviewText(type: string, content: string): string {
  const definition = getArtifactType(type);
  if (definition) {
    return definition.getPreviewText(content);
  }
  return content.slice(0, ARTIFACT_PREVIEW_MAX_CHARS).replace(/\n/g, ' ');
}

/**
 * Returns whether preview text should be truncated with an ellipsis.
 * @param type Artifact MIME type
 * @param content Raw artifact body
 */
export function shouldTruncateArtifactPreview(type: string, content: string): boolean {
  const definition = getArtifactType(type);
  if (definition) {
    return definition.shouldTruncatePreview(content);
  }
  return content.length > ARTIFACT_PREVIEW_MAX_CHARS;
}

/**
 * Builds a safe download filename for an artifact from its title and MIME type.
 * @param type Artifact MIME type
 * @param title Human-readable artifact title
 */
export function artifactDownloadFileName(type: string, title: string): string {
  const base =
    title
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-') || 'artifact';
  const extension = getArtifactType(type)?.fileExtension ?? '';
  return `${base}${extension}`;
}
